import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CircularProgress } from "@heroui/react";

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import {
  CheckoutRoom,
  CheckoutTable,
  canCancelRow,
  canCheckoutRow,
  cancelAllowed,
  checkoutDisabledCode,
  checkoutDisabledText,
  checkoutOffBannerText,
  checkoutOffForHotel,
  checkoutSwitchedOffHotelText,
  checkoutStatusText,
  confirmCheckoutText,
  fetchCheckout,
  lastCheckoutText,
  postCheckout,
  rateLimitedFallbackSeconds,
  refreshIntervalMs,
  roomDisplayName,
  tvHasCheckout,
  tvUnavailableText,
} from '@/features/checkout/checkoutApi';
import {
  needsNoActiveMwLightUserWarning,
  noActiveMwLightUserWarning,
} from '@/features/projects/mwLight';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import { ButtonsModal } from '@/components/Common/Modals';

// ---------------------------------------------------------------------------
// /mw_light/:id — the ONLY page an MW light user ever sees of a project
// (ticket #91, §2.4). A table of rooms, each with its last checkout and a
// Checkout button, and nothing else: no tabs, no settings, no live socket.
//
// There is deliberately NO WebSocket subscription here. The server refuses
// every subscribe an MW light user makes on their MW light project (§4.2), so
// this page polls — fast while something is moving, slowly otherwise, and not
// at all while the browser tab is hidden (§5.7).
// ---------------------------------------------------------------------------

interface ActionHandlers {
  requestCheckout:		(roomNo: string) => void;
  requestCancel:		(roomNo: string) => void;
}

interface MwLightRow {
  room_no:				string;
  room_name:			string;		// "" unless it is a real name (§5.5)
  room:					string;		// what the quick filter and the sort see
  last_checkout:		string;
  status:				string;
  can_checkout:			boolean;
  can_cancel:			boolean;
  tv_ready:				boolean;	// §5.1 — decides whether there is a button at all
  unavailable_text:		string;		// what stands in its place when there is not
  busy:					boolean;
  actions:				React.MutableRefObject<ActionHandlers>;
}

// Room number on the first line, the stored name under it in smaller text —
// but only when it IS a name (§5.5).
const RoomCell = (props: any) => {
  const data = props.data as MwLightRow;

  if (!data)
    return null;

  return (
  <FlexRow direction="column"
           align="start"
           justify="center"
           className="h-full leading-tight">
    <span className="r14 !text-black">
      { data.room_no }
    </span>
  {data.room_name !== "" &&
    <span className="r12 text-[--Dark-Grey]">
      { data.room_name }
    </span>
  }
  </FlexRow>
  );
};

const ActionCell = (props: any) => {
  const { t } = useTranslation();
  const data = props.data as MwLightRow;

  if (!data)
    return null;

  if (data.busy)
    return (
    <FlexRow justify="start" className="h-full">
      <CircularProgress aria-label="Sending checkout" size="sm" />
    </FlexRow>
    );

  // While a request waits, the only useful button is Cancel — pressing
  // Checkout again would return the same request (§5.2).
  if (data.can_cancel)
    return (
    <FlexRow justify="start" className="h-full">
      <Button caption={t('mw_light:cancel')}
              layout="white"
              width="min-w-[96px]"
              onClick={() => data.actions.current.requestCancel(data.room_no)} />
    </FlexRow>
    );

  // §5.1 (rev 4) — a TV that never reported its capabilities gets NO button,
  // not a disabled one: there is nothing to press and nothing to wait for, and
  // a greyed button invites a click that can never work.
  if (!data.tv_ready)
    return (
    <FlexRow justify="start" className="h-full">
      <span className="r12 text-[--Dark-Grey] truncate"
            title={data.unavailable_text}>
        { data.unavailable_text }
      </span>
    </FlexRow>
    );

  // A ready TV always keeps its button, even when the whole table is off
  // (paused, hotel switched off, no right) — disabled, with the page banner
  // carrying the reason.
  return (
  <FlexRow justify="start" className="h-full">
    <Button caption={t('mw_light:checkout')}
            width="min-w-[96px]"
            isDisabled={!data.can_checkout}
            onClick={() => data.actions.current.requestCheckout(data.room_no)} />
  </FlexRow>
  );
};

const MwLight = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const role = React.useContext(RoleContext);
  const logins = useAppSelector((state) => state.logins);
  const projects = useAppSelector((state) => state.projects);

  const [table, setTable] = React.useState(null as CheckoutTable | null);
  // The latest read, for a 409 checkout_disabled to compare against (§0.8).
  const tableRef = React.useRef(table);
  tableRef.current = table;
  const [loading, setLoading] = React.useState(true);
  // The server's own sentence for whatever went wrong last (§3.7). Cleared by
  // the next answer that works.
  const [notice, setNotice] = React.useState("");
  // 404: the project is gone or no longer theirs. The rows go with it.
  const [gone, setGone] = React.useState(false);
  // Retry-After, in ms, from a 429 or 503 — it overrides the normal cadence
  // for exactly one hop.
  const [holdMs, setHoldMs] = React.useState(0);
  const [hidden, setHidden] = React.useState(
    typeof document !== "undefined" ? document.hidden : false
  );
  // Bumped after every completed read so the polling effect re-arms.
  const [tick, setTick] = React.useState(0);
  // Rooms with a POST in flight, so a second click cannot double-send.
  const [busy, setBusy] = React.useState({} as { [roomNo: string]: boolean });
  const [confirmRoom, setConfirmRoom] = React.useState(null as CheckoutRoom | null);

  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // §5.7 — both cadences pause while the tab is hidden. A hotel tablet left on
  // this page all day would otherwise poll for nothing.
  React.useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);

    document.addEventListener("visibilitychange", onVisibility);

    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const applyRow = React.useCallback((room: CheckoutRoom) => {
    setTable((prev) => {
      if (!prev)
        return prev;

      const rooms = prev.rooms.map((r) => r.room_no === room.room_no ? room : r);

      return { ...prev, rooms: rooms };
    });
  }, []);

  const load = React.useCallback(async () => {
    if (!id)
      return;

    const res = await fetchCheckout(id);

    if (!mounted.current)
      return;

    setLoading(false);

    if (res.kind === "ok") {
      setTable(res.table);
      setNotice("");
      setGone(false);
      setHoldMs(0);
      setTick((n) => n + 1);
      return;
    }

    const err = res.error;

    // 401 already put the login screen up through store.dispatchStatus(0);
    // polling on would only stack more of them.
    if (err.status === 401) {
      setNotice("");
      return;
    }

    // §2.4 — a 404 means the project is gone or no longer theirs.
    if (err.status === 404) {
      setGone(true);
      setTable(null);
      setNotice(err.message);
      return;
    }

    // §2.4 — a 503 KEEPS the rows and retries after Retry-After; a 429 waits
    // Retry-After, or 60 s when the header is missing. Both leave `table`
    // exactly as it was: the rows are still true, the server is just busy.
    const seconds =	err.status === 429
					?	(err.retryAfter || rateLimitedFallbackSeconds)
					:	(err.retryAfter || 0);

    setNotice(err.message);
    setHoldMs(seconds > 0 ? seconds * 1000 : 0);
    setTick((n) => n + 1);
  }, [id]);

  // First read.
  React.useEffect(() => {
    setTable(null);
    setLoading(true);
    setNotice("");
    setGone(false);
    setHoldMs(0);
    setBusy({});

    void load();
  }, [id, load]);

  // §2.4 — admins (support) also see the §2.1 warning. It needs the project's
  // executives, which /checkout.json deliberately never sends, so the project
  // read supplies them. An MW light user cannot make this call (403) and does
  // not need to.
  React.useEffect(() => {
    if (	!role.admin ||
			!id
		 )
      return;

    store.getProjects(id);
  }, [role.admin, id]);

  const noActiveWarning = React.useMemo(() => {
    if (!role.admin)
      return false;

    const active = projects.active;

    if (	!active ||
			active.id !== id ||
			!active.limits
		 )
      return false;

    return needsNoActiveMwLightUserWarning(
      active.limits.executives,
      logins.logins.descriptors
    );
  }, [role.admin, projects.active, id, logins.logins.descriptors]);

  const rooms = React.useMemo(() => table ? table.rooms : [], [table]);

  // §6.7 — 5 s while any row is pending / waiting_no_tv, 30 s
  // otherwise; a Retry-After hold wins for one hop.
  const intervalMs = React.useMemo(() => {
    if (holdMs > 0)
      return holdMs;

    return refreshIntervalMs(rooms);
  }, [holdMs, rooms]);

  React.useEffect(() => {
    if (	!id ||
			gone ||
			hidden ||
			intervalMs <= 0
		 )
      return;

    const timer = setTimeout(() => { void load(); }, intervalMs);

    return () => clearTimeout(timer);
  }, [id, gone, hidden, intervalMs, tick, load]);

  const actions = React.useRef({
    requestCheckout:	() => {},
    requestCancel:		() => {},
  } as ActionHandlers);

  const findRoom = React.useCallback((roomNo: string): CheckoutRoom | null => {
    for (const r of rooms) {
      if (r.room_no === roomNo)
        return r;
    }

    return null;
  }, [rooms]);

  const send = React.useCallback(async (room: CheckoutRoom, action: "checkout" | "cancel") => {
    if (!id)
      return;

    setBusy((prev) => ({ ...prev, [room.room_no]: true }));

    const res = await postCheckout({
      projectId:	id,
      roomNo:		room.room_no,
      action:		action,
      checkoutId:	room.checkout ? room.checkout.id : "",
    });

    if (!mounted.current)
      return;

    setBusy((prev) => {
      const next = { ...prev };
      delete next[room.room_no];
      return next;
    });

    if (res.kind === "ok") {
      // §2.4 — the POST answer already carries the new row; there is no need
      // for a GET to find out what we just did.
      applyRow(res.room);
      setNotice("");
      setHoldMs(0);
      setTick((n) => n + 1);
      return;
    }

    const err = res.error;

    if (err.status === 401)
      return;

    // §3.7 — every 409 but project_disabled carries the room's current row.
    // Apply it: the refusal and the truth arrive together.
    if (err.row)
      applyRow(err.row);

    // §3.2 / #91 §0.8 — a press that raced a Checkout switch. When the latest
    // read already says off, its banner carries the sentence, so it is not
    // repeated as a notice. When it still says on, the switch changed
    // meanwhile: the generic sentence, and the table is read again at once so
    // the right banner (server or hotel) comes up.
    if (err.code === checkoutDisabledCode) {
      if (!checkoutOffForHotel(tableRef.current)) {
        setNotice(checkoutDisabledText(tableRef.current));
        void load();
      }
      setTick((n) => n + 1);
      return;
    }

    setNotice(err.message);

    if (	err.status === 429 ||
			err.status === 503
		 ) {
      const seconds = err.retryAfter || (err.status === 429 ? rateLimitedFallbackSeconds : 0);
      setHoldMs(seconds > 0 ? seconds * 1000 : 0);
    }

    setTick((n) => n + 1);
  }, [id, applyRow, load]);

  actions.current.requestCheckout = React.useCallback((roomNo: string) => {
    const room = findRoom(roomNo);

    if (room)
      setConfirmRoom(room);
  }, [findRoom]);

  actions.current.requestCancel = React.useCallback((roomNo: string) => {
    const room = findRoom(roomNo);

    // Cancel needs no confirm: it undoes a request, it does not destroy
    // anything.
    if (room)
      void send(room, "cancel");
  }, [findRoom, send]);

  const canAct =	!!table &&
					table.can_checkout &&
					table.project.enabled;

  // §3.2 — Cancel stays usable while the Checkout is off for the hotel.
  const canCancel =	!!table &&
					cancelAllowed(table.can_checkout, table.project_checkout_enabled, table.project.enabled);

  // #91 §0.8 — "off" comes from project_checkout_enabled, never from
  // checkout_enabled alone: a hotel can be on while System settings is off.
  const checkoutOff = checkoutOffForHotel(table);
  const checkoutOffBanner = checkoutOffBannerText(table);

  const rows = React.useMemo((): MwLightRow[] => {
    return rooms.map((room) => ({
      room_no:			room.room_no,
      room_name:		roomDisplayName(room.room_no, room.name),
      room:				room.room_no + (roomDisplayName(room.room_no, room.name) ? " " + room.name : ""),
      last_checkout:	lastCheckoutText(room.last_checkout),
      status:			checkoutStatusText(room.checkout),
      can_checkout:		canCheckoutRow(room, canAct),
      can_cancel:		canCancelRow(room, canCancel),
      tv_ready:			tvHasCheckout(room.tv),
      unavailable_text:	tvUnavailableText(room.tv),
      busy:				!!busy[room.room_no],
      actions:			actions,
    }));
  }, [rooms, canAct, canCancel, busy, t]);

  // §3.2 — while the Checkout is switched off the Action column only shows
  // disabled buttons and device texts, so it goes; the banner says why. It
  // stays while any row has a waiting request, which can still be cancelled.
  const showAction =	!checkoutOff ||
						rows.some((r) => r.can_cancel);

  const cols = React.useMemo(() => [
    {
      field:		"room",
      headerName:	t("mw_light:grid:room"),
      flex:			0,
      minWidth:		160,
      maxWidth:		220,
      cellRenderer:	RoomCell,
    },
    {
      field:		"last_checkout",
      headerName:	t("mw_light:grid:last_checkout"),
      flex:			0,
      minWidth:		180,
      maxWidth:		220,
    },
    {
      field:		"status",
      headerName:	t("mw_light:grid:status"),
      flex:			1,
      minWidth:		240,
    },
    {
      field:		"action",
      headerName:	t("mw_light:grid:action"),
      flex:			0,
      filter:		false,
      sortable:		false,
      // Wide enough for the button PLUS the longest reason the TV can give
      // ("This TV cannot clear apps"); at 300 it was clipped mid-word, which
      // is exactly the text a user needs to read.
      minWidth:		330,
      maxWidth:		380,
      cellRenderer:	ActionCell,
    },
  ].filter((c) => showAction || c.field !== "action"), [t, showAction]);

  const projectName =	table
						?	table.project.name
						:	"";

  return (
  <React.Fragment>
    <FlexRow direction="column"
             align="start"
             className="pt-[32px] px-[32px] bg-white sticky top-[64px] z-10 w-full">
      <div className="text-[32px]/[48px]">
        {	projectName
        	?	projectName
        	:	t('global:loading')
        }
      </div>
      <div className="r12 text-[--Dark-Grey] pb-[8px]">
        { t('mw_light:subtitle') }
      </div>
    </FlexRow>
    <FlexRow direction="column"
             align="start"
             className="py-[16px] px-[32px] gap-[12px] w-full">
    {/* §2.4 — the hotel is switched off: the rows are still true, but no TV
        can receive anything, and every button is off. */}
    {table && !table.project.enabled &&
      <div className="r14 w-full max-w-[900px] rounded-[4px] bg-[#FDECEA] text-[#B3261E] px-[12px] py-[8px]">
        { t('mw_light:disabled_banner') }
      </div>
    }
    {/* §3.2 / §0.8 — the Checkout is off for this hotel (System settings off
        and the hotel follows it, or the hotel itself is off). The table and
        its statuses stay; every Checkout button is disabled, Cancel is not. */}
    {checkoutOff &&
      <div className="r14 w-full max-w-[900px] rounded-[4px] bg-[#FFF4E5] text-[#8A5300] px-[12px] py-[8px]">
        {	checkoutOffBanner === checkoutSwitchedOffHotelText
        	?	t('mw_light:checkout_off_hotel_banner')
        	:	t('mw_light:checkout_off_banner')
        }
      </div>
    }
    {/* §6.1 — a ready TV keeps its button even when the table is off, disabled,
        "with the page's banner". The hotel-off and Checkout-off cases have
        their own banners above; this covers the others (checkouts paused
        server-wide, or no right), for which the row itself says nothing. */}
    {table && table.project.enabled && !checkoutOff && !table.can_checkout &&
      <div className="r14 w-full max-w-[900px] rounded-[4px] bg-[#FFF4E5] text-[#8A5300] px-[12px] py-[8px]">
        { t('mw_light:checkout_unavailable') }
      </div>
    }
    {noActiveWarning &&
      <div className="r14 w-full max-w-[900px] rounded-[4px] bg-[#FFF4E5] text-[#8A5300] px-[12px] py-[8px]">
        { noActiveMwLightUserWarning }
      </div>
    }
    {notice !== "" &&
      <div className="r14 w-full max-w-[900px] rounded-[4px] bg-[#FDECEA] text-[#B3261E] px-[12px] py-[8px]">
        { notice }
      </div>
    }
    {loading &&
      <CircularProgress aria-label="loading" />
    }
    {!loading && !gone &&
      <div className="w-full">
        <Grid rowData={rows}
              columnDefs={cols}
              filter={true}
              showPages={true}
              pageSize={20}
              maxHeight="calc(100vh - 320px)"
              csvFile="checkout.csv" />
      </div>
    }
    </FlexRow>
  {confirmRoom &&
    <ButtonsModal title={t('mw_light:confirm:title')}
                  message={confirmCheckoutText(confirmRoom.room_no, confirmRoom.name)}
                  buttons={[
                    { value: "no",	caption: t('mw_light:confirm:keep'),	layout: "white" },
                    { value: "yes",	caption: t('mw_light:confirm:yes'),		layout: "warning_bg" },
                  ]}
                  onResult={(_obj: object, answer: string) => {
                    const room = confirmRoom;
                    setConfirmRoom(null);

                    if (	answer === "yes" &&
							room
						 )
                      void send(room, "checkout");
                  }}>
      <>
      </>
    </ButtonsModal>
  }
  </React.Fragment>
  );
};

export default MwLight;
